/**
 * VCC layout class bundles — use 1–2 of these on each feature page.
 *
 * Minimal new page:
 *   <div className={vccPage('my-feature-page')}>
 *     <article className={vccCard()}>
 *       <header className={vccHeader()}>...</header>
 *       <div className={vccUi()}>...</div>
 *     </article>
 *   </div>
 *
 * Legacy pages: keep app-canvas-page / welcome-glass-card — they still work.
 *
 * Tabs / pagination: vccTabs.ts, vccPagination.ts
 */

import { vccTabContent, vccTabNav } from './vccTabs'

/** Page shell — canvas layout + control scale */
export function vccPage(featurePage?: string) {
  return ['vcc-page', 'app-canvas-page', featurePage].filter(Boolean).join(' ')
}

/** Glass content card */
export function vccCard(extra?: string) {
  return ['vcc-card', 'welcome-glass-card', 'vcc-glass-card', extra].filter(Boolean).join(' ')
}

/** Auto-themed form region (inputs, selects, buttons, tables) */
export function vccUi(extra?: string) {
  return ['vcc-ui', 'vcc-form-shell', 'vcc-card-body', extra].filter(Boolean).join(' ')
}

/** Left-aligned label + control row (flex — see common-form.css). */
export function vccFormRow(extra?: string) {
  return ['form-row', extra].filter(Boolean).join(' ')
}

/** Vertical stack of form-row fields. */
export function vccFormStack(extra?: string) {
  return ['vcc-form-stack', extra].filter(Boolean).join(' ')
}

/** @deprecated Prefer vccFormStack + vccFormRow per field. */
export function vccFormGrid(extra?: string) {
  return ['vcc-form-grid', extra].filter(Boolean).join(' ')
}

/** Control cell inside vccFormGrid or search-field rows. */
export function vccFormControl(extra?: string) {
  return ['vcc-form-control', extra].filter(Boolean).join(' ')
}

/** Horizontal action buttons with equal width. */
export function vccFormActions(extra?: string) {
  return ['vcc-form-actions', extra].filter(Boolean).join(' ')
}

/** Card content region — autofit width with min viewport margin (canvas-standards.css) */
export function vccCardBody(extra?: string) {
  return ['vcc-card-body', extra].filter(Boolean).join(' ')
}

/** Panel title block */
export function vccHeader(extra?: string) {
  return ['vcc-header', 'welcome-panel-header', extra].filter(Boolean).join(' ')
}

/** Report/search pages — welcome + company-details shell + optional screen hook (A → B → screen). */
export function vccReportPage(screenClass?: string) {
  const parts = [
    'vcc-page',
    'app-canvas-page',
    'welcome-page',
    'company-details-page',
    screenClass,
  ]
  return parts.filter(Boolean).join(' ')
}

/** Page width and shell contracts kept as explicit CSS hooks. */
export type VccPageWidthHook = 'report' | 'welcome' | 'userDetails' | 'dividendDetails'

/** Standard screen page with an explicit shell contract. */
export function vccScreenPage(screenClass: string, width: VccPageWidthHook = 'report') {
  if (width === 'welcome') {
    return vccPage(['welcome-page', screenClass].filter(Boolean).join(' '))
  }

  const widthClass =
    width === 'userDetails'
      ? 'user-details-page'
      : width === 'dividendDetails'
        ? 'dividend-details-page'
        : ''

  return vccReportPage([screenClass, widthClass].filter(Boolean).join(' '))
}

/** Glass card for report/search canvas pages */
export function vccReportCard(extra?: string) {
  return vccCard(['vcc-report-card', extra].filter(Boolean).join(' '))
}

/** Class bundles for screens with a standard tab rail and report card. */
export function vccTabbedScreen(screenClass: string, width: VccPageWidthHook = 'report') {
  return {
    page: vccScreenPage(screenClass, width),
    tabNav: vccTabNav(),
    tabContent: vccTabContent(),
    card: vccReportCard(),
  } as const
}

/** One-liner for standard feature pages */
export function vccFeaturePage(featurePage: string) {
  return {
    page: vccPage(featurePage),
    card: vccCard(),
    ui: vccUi(),
    header: vccHeader(),
  }
}
